import { useState } from 'react';
import { CasErrorDisplay, CasTable } from 'src/compass/shared/components';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import getCasTableCellValue from 'src/compass/shared/components/CasTable/getCasTableCellValue';
import useRetrieveEntity from 'src/compass/shared/hooks/reactQuery/useRetrieveEntity';
import { YESNOALL_OPTIONS } from 'src/compass/application/general/components/CasYesNoSelect';
import OwnerDialog from './OwnerDialog';

interface IProps {
  entityId?: number
  readOnly?: boolean;
};

export default function OwnersTab({ entityId, readOnly }: IProps) {
  const [dialogRowId, setDialogRowId] = useState<number | null>(null);
  const [reloadTable, setReloadTable] = useState(new Date());

  const { data, isFetching, isError, refetch } = useRetrieveEntity({
    entity: 'Utilities/Owner',
    params: {
      entityId,
    },
    enabled: !!entityId,
  });

  const onClose = () => {
    refetch();
    setDialogRowId(null);
    setReloadTable(new Date());
  };

  const filters = [
    {
      name: 'search',
      label: 'Owner',
    },
    {
      type: 'select',
      name: 'unassignedOnly',
      label: 'Unassigned only?',
      options: YESNOALL_OPTIONS
    },
    {
      type: 'autocomplete',
      name: 'feedLotId',
      label: 'Feedyard',
      url: "FeedYard/FeedLots/Select/List",
    },
    {
      type: 'autocomplete',
      name: 'entityId',
      label: 'Entity',
      url: "Utilities/Entity/List",
    },
  ];

  const columns = [
    {
      field: 'name',
      label: 'Owner',
      render: ({ row, column }: ICasRenderCellProps) => {
        const value = getCasTableCellValue({ row, column });
        if(readOnly) return value;
        return (
          <CasLink onClick={() => setDialogRowId(row.id)}>
            {value}
          </CasLink>
        )
      },
    },
    {
      field: 'feedLotName',
      label: 'Feedyard',
    },
    ...(entityId
      ? []
      : [
          {
            field: 'entityName',
            label: 'Entity',
          },
        ]),
    {
      field: 'metaSource',
    },
    {
      field: 'metaSourceType',
    },
  ];

  const tableProps = entityId
    ? {
        data,
        columns,
      }
    : {
        update: reloadTable,
        entity: 'Utilities/Owner',
        filters,
        columns,
        additionalPayload: { excludeOther: true },
      };

  if (isError) return <CasErrorDisplay message="Error retrieving data from the server." />;

  return (
    <>
      {isFetching && entityId ? <CasLoader /> : <CasTable {...tableProps} />}
      {dialogRowId && <OwnerDialog id={dialogRowId} onClose={onClose} />}
    </>
  );
}
